import { useState, useRef, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { cn } from '../../lib/utils';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, ArrowRight, ArrowLeft, X, Eye, ScrollText, ChevronDown, ChevronUp, Link2, UserPlus, Copy, Ban, HelpCircle, Info } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL || '';

// ── Status label helpers ──
const PARSE_LABEL = { completed: 'Parsed', failed: 'Parse Failed', pending: 'Pending' };
const LINK_LABEL = { linked_existing: 'Linked Existing', created_new: 'Created New', duplicate_document: 'Duplicate Doc', failed: 'Link Failed', skipped: 'Skipped', not_linked: 'Not Linked' };

const ParseBadge = ({ status }) => (
  <Badge variant="outline" className={cn("text-[9px] px-1.5 h-4 flex-shrink-0",
    status === 'completed' ? "border-green-200 text-green-700 bg-green-50" :
    status === 'failed' ? "border-red-200 text-red-700 bg-red-50" : "border-slate-200 text-slate-500"
  )} data-testid={`parse-badge-${status}`}>
    {PARSE_LABEL[status] || status}
  </Badge>
);

const LinkBadge = ({ status }) => (
  <Badge variant="outline" className={cn("text-[9px] px-1.5 h-4 flex-shrink-0",
    status === 'linked_existing' ? "border-blue-200 text-blue-700 bg-blue-50" :
    status === 'created_new' ? "border-green-200 text-green-700 bg-green-50" :
    status === 'duplicate_document' ? "border-amber-200 text-amber-700 bg-amber-50" :
    status === 'failed' ? "border-red-200 text-red-700 bg-red-50" :
    "border-slate-200 text-slate-500"
  )} data-testid={`link-badge-${status}`}>
    {LINK_LABEL[status] || status}
  </Badge>
);

const CCDAImportModal = ({ open, onOpenChange, initialFiles }) => {
  const { authFetch } = useAuth();
  const fileRef = useRef(null);
  const pollRef = useRef(null);
  const [step, setStep] = useState('pick'); // pick | uploading | processing | done | failed | history | file_detail | linkage
  const [files, setFiles] = useState([]);
  const [batchResult, setBatchResult] = useState(null);
  const [batches, setBatches] = useState([]);
  const [fileDetail, setFileDetail] = useState(null);
  const [fileLog, setFileLog] = useState(null);
  const [linkageResult, setLinkageResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const initialFilesConsumed = useRef(false);

  useEffect(() => {
    if (open) {
      setStep('pick'); setFiles([]); setBatchResult(null); setFileDetail(null); setFileLog(null); setLinkageResult(null);
      initialFilesConsumed.current = false;
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [open]);

  // Auto-trigger upload when initialFiles are provided
  useEffect(() => {
    if (open && initialFiles && initialFiles.length > 0 && !initialFilesConsumed.current) {
      initialFilesConsumed.current = true;
      setFiles(initialFiles);
      // Trigger upload with provided files
      uploadFiles(initialFiles);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFiles]);

  const handleFiles = (e) => {
    const picked = Array.from(e.target.files || []).slice(0, 10);
    setFiles(picked);
  };

  const _httpErrorMsg = (status, fallback) => {
    if (status === 401) return 'Session expired — please log in again';
    if (status === 422) return fallback || 'Invalid file format — only XML/CDA files accepted';
    if (status === 429) return 'Too many requests — please wait a moment and retry';
    if (status >= 500) return 'Server error — please try again shortly';
    return fallback || `Upload failed (${status})`;
  };

  const uploadFiles = async (filesToUpload) => {
    if (!filesToUpload || filesToUpload.length === 0) return;
    setStep('uploading');
    setBusy(true);

    const formData = new FormData();
    for (const f of filesToUpload) formData.append('files', f);

    try {
      const res = await authFetch(`${API}/api/ccda/upload`, { method: 'POST', body: formData, headers: {} });
      let data = null;
      try { data = await res.json(); } catch {}

      if (data?.batch_id) {
        setBatchResult({ ...data, files: filesToUpload.map(f => ({ filename: f.name, status: 'pending' })) });
        setStep('processing');
        _startPolling(data.batch_id);
      } else {
        toast.error(_httpErrorMsg(res.status, data?.detail));
        setStep('pick');
      }
    } catch {
      toast.error('Network error — check your connection and try again');
      setStep('pick');
    }
    setBusy(false);
  };

  const handleUpload = () => uploadFiles(files);

  const _startPolling = (batchId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    let failCount = 0;
    const poll = setInterval(async () => {
      try {
        const res = await authFetch(`${API}/api/ccda/batch/${batchId}`);
        if (!res.ok) {
          failCount++;
          if (failCount >= 5) {
            clearInterval(poll); pollRef.current = null;
            toast.error(_httpErrorMsg(res.status, 'Lost connection to batch status'));
            setStep('done');
          }
          return;
        }
        failCount = 0;
        const data = await res.json();
        setBatchResult(data);

        if (data.status === 'completed' || data.status === 'partial' || data.status === 'failed') {
          clearInterval(poll);
          pollRef.current = null;
          setStep('done');
          if (data.status === 'completed') toast.success(`C-CDA batch complete: ${data.completed} files parsed`);
          else if (data.status === 'partial') toast.warning(`C-CDA batch partial: ${data.completed} parsed, ${data.failed} failed`);
          else toast.error('C-CDA batch failed');
        }
      } catch {
        failCount++;
        if (failCount >= 5) {
          clearInterval(poll); pollRef.current = null;
          toast.error('Network error — batch may still be processing');
          setStep('done');
        }
      }
    }, 1500);
    pollRef.current = poll;
  };

  // ── History ──
  const loadHistory = useCallback(async () => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/api/ccda/batches`);
      if (res.ok) {
        const data = await res.json();
        setBatches(data.batches || []);
      } else {
        toast.error(_httpErrorMsg(res.status, 'Could not load history'));
      }
    } catch { toast.error('Network error loading history'); }
    setBusy(false);
  }, [authFetch]);

  const openBatchDetail = async (batchId) => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/api/ccda/batch/${batchId}`);
      if (res.ok) {
        const data = await res.json();
        setBatchResult(data);
        setStep('done');
      } else {
        toast.error(_httpErrorMsg(res.status, 'Could not load batch'));
      }
    } catch { toast.error('Network error loading batch'); }
    setBusy(false);
  };

  // ── File detail / log ──
  const openFilePretty = async (fileId) => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/api/ccda/file/${fileId}/pretty`);
      if (res.ok) {
        const data = await res.json();
        setFileDetail(data);
        setStep('file_detail');
      } else {
        toast.error(_httpErrorMsg(res.status, 'Could not load file details'));
      }
    } catch { toast.error('Network error loading file'); }
    setBusy(false);
  };

  const openFileLog = async (fileId) => {
    try {
      const res = await authFetch(`${API}/api/ccda/file/${fileId}/log`);
      if (res.ok) {
        const data = await res.json();
        setFileLog(data);
      } else {
        toast.error(_httpErrorMsg(res.status, 'Could not load log'));
      }
    } catch { toast.error('Network error loading log'); }
  };

  // ── Patient linkage ──
  const handleLinkPatients = async (batchId) => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/api/ccda/batch/${batchId}/link-patients`, { method: 'POST' });
      if (res.ok) {
        const data = await res.json();
        setLinkageResult(data);
        setStep('linkage');
        const total = (data.linked_existing || 0) + (data.created_new || 0);
        if (total > 0) toast.success(`Linked ${total} patient${total > 1 ? 's' : ''}`);
        else if (data.duplicate_documents > 0) toast.info('All files are duplicate documents');
        else if (data.failed > 0) toast.warning('Patient linkage had failures');
      } else {
        toast.error(_httpErrorMsg(res.status, 'Could not link patients'));
      }
    } catch { toast.error('Network error during patient linkage'); }
    setBusy(false);
  };

  const loadLinkageStatus = async (batchId) => {
    setBusy(true);
    try {
      const res = await authFetch(`${API}/api/ccda/batch/${batchId}/linkage-status`);
      if (res.ok) {
        const data = await res.json();
        setLinkageResult(data);
        setStep('linkage');
      } else if (res.status === 404) {
        toast.info('No linkage data for this batch');
      } else {
        toast.error(_httpErrorMsg(res.status, 'Could not load linkage status'));
      }
    } catch { toast.error('Network error loading linkage'); }
    setBusy(false);
  };

  const fmtDate = (ts) => {
    if (!ts) return '—';
    try { return new Date(ts).toLocaleString(); } catch { return ts; }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="ccda-import-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5" /> Import C-CDA Documents</DialogTitle>
          <DialogDescription>Upload XML C-CDA files for automated clinical data extraction</DialogDescription>
        </DialogHeader>

        {/* Step: Pick files */}
        {step === 'pick' && (
          <div className="space-y-4">
            <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center cursor-pointer hover:border-slate-400 transition-colors"
              onClick={() => fileRef.current?.click()} data-testid="ccda-dropzone">
              <Upload className="h-8 w-8 text-slate-300 mx-auto mb-2" />
              <p className="text-sm text-slate-600">Select up to 10 XML files</p>
              <p className="text-xs text-slate-400 mt-1">Accepted: .xml, .ccda, .cda</p>
            </div>
            <input ref={fileRef} type="file" multiple accept=".xml,.ccda,.cda,.txt" className="hidden" onChange={handleFiles} data-testid="ccda-file-input" />

            {files.length > 0 && (
              <div className="space-y-1" data-testid="ccda-file-list">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-600 px-2 py-1 rounded bg-slate-50">
                    <FileText className="h-3 w-3" /> {f.name}
                    <span className="text-slate-400 ml-auto">{(f.size / 1024).toFixed(0)} KB</span>
                  </div>
                ))}
              </div>
            )}

            <DialogFooter className="flex items-center justify-between">
              <Button variant="outline" size="sm" onClick={() => { setStep('history'); loadHistory(); }} data-testid="ccda-history-btn">
                <ScrollText className="h-3.5 w-3.5 mr-1" /> View History
              </Button>
              <Button onClick={handleUpload} disabled={files.length === 0} className="bg-black hover:bg-gray-800 gap-2" data-testid="ccda-upload-btn">
                Upload {files.length > 0 ? `${files.length} File${files.length > 1 ? 's' : ''}` : ''} <ArrowRight className="h-3.5 w-3.5" />
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Uploading */}
        {step === 'uploading' && (
          <div className="py-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
            <p className="text-sm text-slate-600">Uploading {files.length} file{files.length > 1 ? 's' : ''}...</p>
          </div>
        )}

        {/* Step: Processing */}
        {step === 'processing' && (
          <div className="py-6 space-y-4" data-testid="ccda-processing">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto mb-3" />
              <p className="text-sm font-medium text-slate-700">Parsing C-CDA documents...</p>
            </div>
            {batchResult && (
              <div className="flex items-center justify-center gap-4 text-xs text-slate-500">
                <span>{batchResult.completed || 0} / {batchResult.total_files || 0} completed</span>
                {batchResult.failed > 0 && <span className="text-red-500">{batchResult.failed} failed</span>}
              </div>
            )}
          </div>
        )}

        {/* Step: Done */}
        {step === 'done' && batchResult && (
          <div className="space-y-4" data-testid="ccda-result">
            {/* Stage A banner: Parse outcome */}
            <div className={cn("rounded-lg border p-4 text-center",
              batchResult.status === 'completed' ? "bg-green-50 border-green-200" :
              batchResult.status === 'partial' ? "bg-amber-50 border-amber-200" :
              "bg-red-50 border-red-200"
            )}>
              {batchResult.status === 'completed'
                ? <CheckCircle2 className="h-8 w-8 text-green-600 mx-auto mb-2" />
                : <AlertCircle className="h-8 w-8 text-amber-500 mx-auto mb-2" />
              }
              <p className={cn("text-sm font-semibold",
                batchResult.status === 'completed' ? "text-green-800" : "text-amber-800"
              )} data-testid="ccda-parse-headline">
                {batchResult.status === 'completed' ? 'All Files Parsed (Ready for Linking)' :
                 batchResult.status === 'partial' ? 'Parsing Complete with Errors' : 'Parsing Failed'}
              </p>
              <p className="text-[11px] text-slate-500 mt-1.5 leading-relaxed" data-testid="ccda-stage-subtext">
                Parsing validates XML and extracts clinical data. Duplicate-document checks occur during Link&nbsp;to&nbsp;Patients.
              </p>
            </div>

            {/* Parse summary chips */}
            <div className="flex items-center justify-center gap-3 flex-wrap" data-testid="parse-summary-chips">
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-green-50 border border-green-200">
                <CheckCircle2 className="h-3 w-3 text-green-600" />
                <span className="text-xs font-medium text-green-700" data-testid="parse-count-parsed">{batchResult.completed || 0} Parsed</span>
              </div>
              {(batchResult.failed || 0) > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-red-50 border border-red-200">
                  <AlertCircle className="h-3 w-3 text-red-500" />
                  <span className="text-xs font-medium text-red-700" data-testid="parse-count-failed">{batchResult.failed} Parse Failed</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-50 border border-slate-200">
                <FileText className="h-3 w-3 text-slate-500" />
                <span className="text-xs font-medium text-slate-600" data-testid="parse-count-total">{batchResult.total_files || 0} Total</span>
              </div>
            </div>

            {/* Per-file results with 2-stage status */}
            {batchResult.files?.length > 0 && (
              <div className="space-y-1.5 max-h-[200px] overflow-y-auto" data-testid="ccda-file-results">
                {batchResult.files.map((f, i) => (
                  <div key={f.file_id || i} className={cn("flex items-center gap-2 rounded-lg border px-3 py-2",
                    f.status === 'completed' ? "bg-green-50/50 border-green-100" : f.status === 'failed' ? "bg-red-50/50 border-red-100" : "bg-slate-50 border-slate-100"
                  )}>
                    {f.status === 'completed' ? <CheckCircle2 className="h-3.5 w-3.5 text-green-600 flex-shrink-0" /> :
                     f.status === 'failed' ? <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" /> :
                     <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin flex-shrink-0" />}
                    <span className="text-xs text-slate-700 truncate flex-1">{f.filename}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <ParseBadge status={f.status} />
                      <LinkBadge status="not_linked" />
                    </div>
                    {f.error && <span className="text-[10px] text-red-500 truncate max-w-[120px]">{f.error}</span>}
                    {f.file_id && f.status === 'completed' && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button className="text-[10px] text-slate-500 hover:text-black underline" onClick={() => openFilePretty(f.file_id)} data-testid={`view-file-${f.file_id}`}>
                          <Eye className="h-3 w-3 inline mr-0.5" />View
                        </button>
                        <button className="text-[10px] text-slate-500 hover:text-black underline" onClick={() => openFileLog(f.file_id)} data-testid={`log-file-${f.file_id}`}>
                          <ScrollText className="h-3 w-3 inline mr-0.5" />Log
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Ingestion log (if loaded) */}
            {fileLog && (
              <div className="border rounded-lg bg-slate-50 p-2.5 max-h-[100px] overflow-y-auto" data-testid="ccda-file-log">
                <p className="text-xs font-medium text-slate-700 mb-1">Log: {fileLog.filename}</p>
                {(fileLog.log || []).map((line, i) => (
                  <p key={i} className="text-[10px] text-slate-500 font-mono">{line}</p>
                ))}
              </div>
            )}

            {/* Status legend */}
            <StatusLegend />

            <DialogFooter className="flex items-center justify-between">
              <Button variant="outline" onClick={() => { setStep('pick'); setFiles([]); setBatchResult(null); setFileLog(null); setLinkageResult(null); }} data-testid="ccda-new-import-btn">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> New Import
              </Button>
              <div className="flex items-center gap-2">
                {batchResult.batch_id && (batchResult.completed || 0) > 0 && (
                  <Button variant="outline" className="gap-1.5" onClick={() => handleLinkPatients(batchResult.batch_id)} disabled={busy} data-testid="ccda-link-patients-btn">
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Link2 className="h-3.5 w-3.5" />}
                    Link to Patients
                  </Button>
                )}
                <Button onClick={() => onOpenChange(false)} className="bg-black hover:bg-gray-800" data-testid="ccda-done-btn">Done</Button>
              </div>
            </DialogFooter>
          </div>
        )}

        {/* Step: File Detail (pretty view) */}
        {step === 'file_detail' && fileDetail && (
          <div className="space-y-4" data-testid="ccda-file-detail">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">{fileDetail.filename}</h3>
              <Badge variant="outline" className={cn("text-[10px]",
                fileDetail.status === 'completed' ? "text-green-700 border-green-300" : "text-red-600 border-red-200"
              )}>{fileDetail.status}</Badge>
            </div>

            {fileDetail.extracted && (
              <div className="space-y-3 max-h-[350px] overflow-y-auto">
                {Object.entries(fileDetail.extracted).map(([section, data]) => (
                  <SectionCard key={section} title={section} data={data} />
                ))}
              </div>
            )}

            {fileDetail.error && (
              <div className="border border-red-200 rounded-lg bg-red-50 p-3">
                <p className="text-xs text-red-600">{fileDetail.error}</p>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('done')} data-testid="ccda-back-to-results">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Results
              </Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: Linkage Results */}
        {step === 'linkage' && linkageResult && (
          <div className="space-y-4" data-testid="ccda-linkage">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
              <Link2 className="h-6 w-6 text-slate-500 mx-auto mb-1.5" />
              <p className="text-sm font-semibold text-slate-700">Patient Linkage Complete</p>
              <p className="text-[11px] text-slate-500 mt-1">Parsed files have been matched against existing patients or created as new records.</p>
            </div>

            {/* Linkage summary chips */}
            <div className="flex items-center justify-center gap-3 text-sm flex-wrap" data-testid="linkage-summary-chips">
              {(linkageResult.linked_existing || 0) > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-blue-50 border border-blue-200" data-testid="linkage-linked-existing">
                  <Link2 className="h-3 w-3 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">{linkageResult.linked_existing} Linked Existing</span>
                </div>
              )}
              {(linkageResult.created_new || 0) > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-green-50 border border-green-200" data-testid="linkage-created-new">
                  <UserPlus className="h-3 w-3 text-green-600" />
                  <span className="text-xs font-medium text-green-700">{linkageResult.created_new} Created New</span>
                </div>
              )}
              {(linkageResult.duplicate_documents || 0) > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-amber-50 border border-amber-200" data-testid="linkage-duplicate-docs">
                  <Copy className="h-3 w-3 text-amber-600" />
                  <span className="text-xs font-medium text-amber-700">{linkageResult.duplicate_documents} Duplicate Docs</span>
                </div>
              )}
              {(linkageResult.failed || 0) > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-red-50 border border-red-200" data-testid="linkage-failed">
                  <AlertCircle className="h-3 w-3 text-red-500" />
                  <span className="text-xs font-medium text-red-700">{linkageResult.failed} Link Failed</span>
                </div>
              )}
              {(linkageResult.already_linked || 0) > 0 && (
                <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-100 border border-slate-200" data-testid="linkage-already-linked">
                  <CheckCircle2 className="h-3 w-3 text-slate-500" />
                  <span className="text-xs font-medium text-slate-600">{linkageResult.already_linked} Already Linked</span>
                </div>
              )}
            </div>

            {/* Per-file linkage details with 2-stage status */}
            {linkageResult.results?.length > 0 && (
              <div className="space-y-1.5 max-h-[220px] overflow-y-auto" data-testid="linkage-details">
                {linkageResult.results.map((r, i) => (
                  <div key={r.file_id || i} className={cn("rounded-lg border overflow-hidden",
                    r.linkage_status === 'linked_existing' ? "border-blue-100" :
                    r.linkage_status === 'created_new' ? "border-green-100" :
                    r.linkage_status === 'duplicate_document' ? "border-amber-100" :
                    r.linkage_status === 'failed' ? "border-red-100" : "border-slate-100"
                  )}>
                    <div className={cn("flex items-center gap-2 px-3 py-2",
                      r.linkage_status === 'linked_existing' ? "bg-blue-50/50" :
                      r.linkage_status === 'created_new' ? "bg-green-50/50" :
                      r.linkage_status === 'duplicate_document' ? "bg-amber-50/50" :
                      r.linkage_status === 'failed' ? "bg-red-50/50" : "bg-slate-50"
                    )}>
                      {r.linkage_status === 'linked_existing' ? <Link2 className="h-3.5 w-3.5 text-blue-600 flex-shrink-0" /> :
                       r.linkage_status === 'created_new' ? <UserPlus className="h-3.5 w-3.5 text-green-600 flex-shrink-0" /> :
                       r.linkage_status === 'duplicate_document' ? <Copy className="h-3.5 w-3.5 text-amber-500 flex-shrink-0" /> :
                       r.linkage_status === 'failed' ? <AlertCircle className="h-3.5 w-3.5 text-red-500 flex-shrink-0" /> :
                       <Ban className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />}
                      <span className="text-xs text-slate-700 truncate flex-1">{r.filename}</span>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <ParseBadge status={r.parse_status || 'completed'} />
                        <LinkBadge status={r.linkage_status} />
                      </div>
                    </div>
                    {r.patient_id && (
                      <div className="px-3 py-1 bg-blue-50/30 border-t border-blue-100/50 flex items-center gap-2">
                        <span className="text-[9px] text-blue-600">Patient:</span>
                        <span className="text-[9px] font-mono text-blue-800">{r.patient_id.slice(0, 12)}</span>
                        {r.patient_match_reason && <span className="text-[9px] text-blue-400 ml-auto">{r.patient_match_reason}</span>}
                      </div>
                    )}
                    {r.error && (
                      <div className="px-3 py-1 border-t border-red-100/50">
                        <span className="text-[9px] text-red-500">{r.error}</span>
                      </div>
                    )}
                    {r.duplicate_reason && (
                      <div className="px-3 py-1 border-t border-amber-100/50">
                        <span className="text-[9px] text-amber-600">{r.duplicate_reason}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <StatusLegend />

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('done')} data-testid="ccda-back-from-linkage">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back to Results
              </Button>
              <Button onClick={() => onOpenChange(false)} className="bg-black hover:bg-gray-800" data-testid="ccda-linkage-done-btn">Done</Button>
            </DialogFooter>
          </div>
        )}

        {/* Step: History */}
        {step === 'history' && (
          <div className="space-y-3" data-testid="ccda-history">
            {busy && (
              <div className="py-4 text-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400 mx-auto" /></div>
            )}
            {!busy && batches.length === 0 && (
              <div className="py-6 text-center text-sm text-slate-400">No import history yet</div>
            )}
            {!busy && batches.map((b, i) => (
              <div key={b.batch_id || i} className="rounded-lg border border-slate-200 p-3 space-y-1.5 hover:border-slate-300 cursor-pointer"
                onClick={() => openBatchDetail(b.batch_id)} data-testid={`ccda-batch-${i}`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-slate-500">{fmtDate(b.created_at)}</span>
                  <div className="flex items-center gap-1.5">
                    <Badge variant="outline" className={cn("text-[10px]",
                      b.status === 'completed' ? "text-green-700 border-green-300" :
                      b.status === 'partial' ? "text-amber-600 border-amber-200" :
                      "text-red-600 border-red-200"
                    )}>{b.status === 'completed' ? 'Parsed' : b.status === 'partial' ? 'Partial' : 'Failed'}</Badge>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-slate-600">{b.total_files || 0} files</span>
                  <span className="text-green-700">{b.completed || 0} parsed</span>
                  {(b.failed || 0) > 0 && <span className="text-red-600">{b.failed} parse failed</span>}
                </div>
              </div>
            ))}

            <DialogFooter>
              <Button variant="outline" onClick={() => setStep('pick')} data-testid="ccda-back-to-pick">
                <ArrowLeft className="h-3.5 w-3.5 mr-1" /> Back
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};


// ── Helper: collapsible section for extracted data ──
const SectionCard = ({ title, data }) => {
  const [expanded, setExpanded] = useState(false);
  const label = title.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

  const renderValue = (val) => {
    if (val === null || val === undefined) return <span className="text-slate-300">—</span>;
    if (typeof val === 'object' && !Array.isArray(val)) {
      return (
        <div className="pl-2 border-l border-slate-200 ml-1 space-y-0.5">
          {Object.entries(val).map(([k, v]) => (
            <div key={k} className="flex gap-1">
              <span className="text-slate-400 flex-shrink-0">{k}:</span>
              <span className="text-slate-700">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
            </div>
          ))}
        </div>
      );
    }
    return <span className="text-slate-700">{String(val)}</span>;
  };

  const itemCount = Array.isArray(data) ? data.length : (data && typeof data === 'object' ? Object.keys(data).length : 1);

  return (
    <div className="border rounded-lg overflow-hidden">
      <button className="w-full flex items-center justify-between px-3 py-2 bg-slate-50 hover:bg-slate-100 transition-colors"
        onClick={() => setExpanded(v => !v)}>
        <span className="text-xs font-medium text-slate-700">{label}</span>
        <div className="flex items-center gap-1.5">
          <Badge variant="outline" className="text-[9px] px-1 h-4">{itemCount}</Badge>
          {expanded ? <ChevronUp className="h-3 w-3 text-slate-400" /> : <ChevronDown className="h-3 w-3 text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div className="px-3 py-2 space-y-1 text-[11px] max-h-[150px] overflow-y-auto">
          {Array.isArray(data) ? data.map((item, i) => (
            <div key={i} className="py-1 border-b border-slate-50 last:border-0">{renderValue(item)}</div>
          )) : renderValue(data)}
        </div>
      )}
    </div>
  );
};


export default CCDAImportModal;


// ── Status Legend (collapsible help tooltip) ──
const StatusLegend = () => {
  const [show, setShow] = useState(false);
  return (
    <div className="border rounded-lg overflow-hidden" data-testid="status-legend">
      <button className="w-full flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 hover:bg-slate-100 transition-colors text-left"
        onClick={() => setShow(v => !v)}>
        <HelpCircle className="h-3 w-3 text-slate-400" />
        <span className="text-[10px] text-slate-500 font-medium">What do these statuses mean?</span>
        {show ? <ChevronUp className="h-3 w-3 text-slate-400 ml-auto" /> : <ChevronDown className="h-3 w-3 text-slate-400 ml-auto" />}
      </button>
      {show && (
        <div className="px-3 py-2 space-y-1.5 bg-white border-t border-slate-100" data-testid="status-legend-content">
          <p className="text-[10px] font-semibold text-slate-600 mb-1">Stage A: Parse</p>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[8px] px-1 h-3.5 border-green-200 text-green-700">Parsed</Badge>
            <span className="text-[10px] text-slate-500">XML is valid and clinical data was extracted</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[8px] px-1 h-3.5 border-red-200 text-red-700">Parse Failed</Badge>
            <span className="text-[10px] text-slate-500">XML is invalid or extraction failed</span>
          </div>
          <p className="text-[10px] font-semibold text-slate-600 mt-2 mb-1">Stage B: Linkage</p>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[8px] px-1 h-3.5 border-blue-200 text-blue-700">Linked Existing</Badge>
            <span className="text-[10px] text-slate-500">Matched to an existing patient record</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[8px] px-1 h-3.5 border-green-200 text-green-700">Created New</Badge>
            <span className="text-[10px] text-slate-500">New patient record created from this document</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[8px] px-1 h-3.5 border-amber-200 text-amber-700">Duplicate Doc</Badge>
            <span className="text-[10px] text-slate-500">Same clinical document was already imported</span>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-[8px] px-1 h-3.5 border-slate-200 text-slate-500">Not Linked</Badge>
            <span className="text-[10px] text-slate-500">Linkage has not been run yet</span>
          </div>
        </div>
      )}
    </div>
  );
};
